import { cva, type VariantProps } from 'class-variance-authority';

/**
 * Solid palette fills. Each paints the palette mid-tone behind the glyph and
 * draws the glyph in that palette's on-fill color, so these backgrounds own
 * the glyph color.
 */
const ICON_SOLID_FILL_CLASSES = {
  primaryFill: 'bg-primary-500 text-primary-on-fill',
  secondaryFill: 'bg-secondary-500 text-secondary-on-fill',
  tertiaryFill: 'bg-tertiary-500 text-tertiary-on-fill',
  accentFill: 'bg-accent-500 text-accent-on-fill',
  neutralFill: 'bg-neutral-500 text-neutral-on-fill',
  successFill: 'bg-success-500 text-success-on-fill',
  warningFill: 'bg-warning-500 text-warning-on-fill',
  errorFill: 'bg-error-500 text-error-on-fill',
  infoFill: 'bg-info-500 text-info-on-fill',
} as const;

const ICON_BACKGROUND_CLASSES = {
  default: 'bg-transparent',
  neutral: 'bg-neutral-100',
  primary: 'bg-primary-100',
  secondary: 'bg-secondary-100',
  black: 'bg-black',
  ...ICON_SOLID_FILL_CLASSES,
} as const;

const ICON_SHAPE_CLASSES = {
  circle: 'rounded-full',
  'rounded-square': 'rounded-md',
} as const;

type IconBackground = keyof typeof ICON_BACKGROUND_CLASSES;
type IconShape = keyof typeof ICON_SHAPE_CLASSES;

export type IconSolidFillBackground = keyof typeof ICON_SOLID_FILL_CLASSES;

/** Backgrounds that paint a chip behind the glyph. */
const ICON_CHIP_BACKGROUNDS = (
  Object.keys(ICON_BACKGROUND_CLASSES) as IconBackground[]
).filter((background) => background !== 'default');

export function isIconSolidFill(
  background: IconBackground | null | undefined,
): background is IconSolidFillBackground {
  return background != null && background in ICON_SOLID_FILL_CLASSES;
}

/**
 * HEN Icon variants using HEN Design System tokens.
 * All styling references design tokens defined in tailwind.config.ts.
 */
export const iconVariants = cva(
  [
    'inline-block',
    'shrink-0',
    'aspect-square',
    'stroke-current',
    'fill-none',
    'vertical-align-middle',
  ],
  {
    variants: {
      size: {
        // TODO: Check with creative on these sizes
        full: 'w-full h-full',
        sm: 'w-4 h-4',
        'sm-md': 'w-5 h-5',
        md: 'w-6 h-6',
        lg: 'w-8 h-8',
        xl: 'w-10 h-10',
        '2xl': 'w-12 h-12',
        '3xl': 'w-14 h-14',
        '4xl': 'w-16 h-16',
      },
      color: {
        // TODO: Check with creative on these colors
        default: 'text-current',
        // Same token as default `Heading` title color
        heading: 'text-heading',
        white: 'text-white',
        primary: 'text-primary-700',
        secondary: 'text-secondary-700',
        black: 'text-black',
        neutral: 'text-neutral-700',
        warning: 'text-warning-900',
        error: 'text-error-500',
        success: 'text-success-500',
      },
      // TODO: Check with creative on these background colors
      background: ICON_BACKGROUND_CLASSES,
      shape: {
        circle: '',
        'rounded-square': '',
      } satisfies Record<IconShape, string>,
    },
    compoundVariants: [
      // A rounded, padded <svg> clips its glyph to the inner corner curve, so
      // chips keep overflow visible to draw glyphs that reach the corners.
      ...(Object.entries(ICON_SHAPE_CLASSES) as Array<[IconShape, string]>).map(
        ([shape, radius]) => ({
          background: ICON_CHIP_BACKGROUNDS,
          shape,
          class: `${radius} overflow-visible`,
        }),
      ),
      // Adjust icon size + add padding to maintain visual size
      // sm: 16px icon → need 24px container (16px + 8px padding)
      {
        background: ICON_CHIP_BACKGROUNDS,
        size: 'sm',
        class: 'w-6 h-6 p-1', // 24px total (icon appears ~16px)
      },
      {
        background: ICON_CHIP_BACKGROUNDS,
        size: 'sm-md',
        class: 'w-9 h-9 p-2', // 36px total (icon appears ~20px)
      },
      // md: 24px icon → need 36px container (24px + 12px padding)
      {
        background: ICON_CHIP_BACKGROUNDS,
        size: 'md',
        class: 'w-9 h-9 p-1.5', // 36px total (icon appears ~24px)
      },
      // lg: 32px icon → need 40px container (32px + 8px padding)
      {
        background: ICON_CHIP_BACKGROUNDS,
        size: 'lg',
        class: 'w-10 h-10 p-1', // 40px total (icon appears ~32px)
      },
      // xl: 40px icon → need 48px container (40px + 8px padding)
      {
        background: ICON_CHIP_BACKGROUNDS,
        size: 'xl',
        class: 'w-12 h-12 p-1', // 48px total (icon appears ~40px)
      },
      {
        background: ICON_CHIP_BACKGROUNDS,
        size: '2xl',
        class: 'w-14 h-14 p-1', // 56px total (icon appears ~48px)
      },
      {
        background: ICON_CHIP_BACKGROUNDS,
        size: '3xl',
        class: 'w-16 h-16 p-1', // 64px total (icon appears ~56px)
      },
      {
        background: ICON_CHIP_BACKGROUNDS,
        size: '4xl',
        class: 'w-[4.5rem] h-[4.5rem] p-1', // 72px total (icon appears ~64px)
      },
    ],
    defaultVariants: {
      size: 'md',
      color: 'default',
      background: 'default',
      shape: 'circle',
    },
  },
);

export const iconBackgroundContainerVariants = cva(
  [
    'inline-flex',
    'shrink-0',
    'items-center',
    'justify-center',
    'leading-none',
    'align-middle',
  ],
  {
    variants: {
      size: {
        full: 'size-full',
        sm: 'size-6',
        'sm-md': 'size-9',
        md: 'size-9',
        lg: 'size-10',
        xl: 'size-12',
        '2xl': 'size-14',
        '3xl': 'size-16',
        '4xl': 'h-[4.5rem] w-[4.5rem]',
      },
      background: ICON_BACKGROUND_CLASSES,
      shape: ICON_SHAPE_CLASSES,
    },
    defaultVariants: {
      size: 'md',
      background: 'default',
      shape: 'circle',
    },
  },
);

export type IconVariantProps = VariantProps<typeof iconVariants>;
